import {
  AlignmentType,
  BorderStyle,
  Paragraph,
  TextRun,
} from 'docx';
import { Quote } from '../../interfaces/quote.interface';
import {
  SalesLevelRateEntryDto,
  SalesLevelRatesDto,
  SalesQuotePriceListResponseDto,
  UsersTenantResponseDto,
} from '@o2-fe/api';

/**
 * Color constants for document styling
 */
export const DOCUMENT_COLORS = {
  text: '000000',
  gray: '666666',
  lightGray: 'F5F5F5',
  line: '000000',
};

/**
 * Margin constants in DXA units (1440 DXA = 1 inch = 25.4mm)
 */
export const DOCUMENT_MARGINS = {
  top: 800, // ~14mm
  bottom: 850, // ~15mm
  left: 1134, // ~20mm
  right: 1134, // ~20mm
};

/**
 * No border style for invisible table cells
 */
export const noBorder = {
  style: BorderStyle.NONE,
  size: 0,
  color: 'FFFFFF',
};

/**
 * Invisible borders for all sides of table cells
 */
export const invisibleBorders = {
  top: noBorder,
  bottom: noBorder,
  left: noBorder,
  right: noBorder,
};

/**
 * Bottom border style (single line, 8pt)
 */
export const bottomBorder = {
  style: BorderStyle.SINGLE,
  size: 8,
  color: DOCUMENT_COLORS.text,
};

/**
 * Bottom-only borders for table cells
 */
export const bottomOnlyBorders = {
  top: noBorder,
  bottom: bottomBorder,
  left: noBorder,
  right: noBorder,
};

/**
 * Options for text styling
 */
export interface TextOptions {
  size?: number;
  bold?: boolean;
  color?: string;
}

/**
 * Options for paragraph styling
 */
export interface ParagraphOptions extends TextOptions {
  after?: number;
  before?: number;
  line?: number;
  alignment?: typeof AlignmentType[keyof typeof AlignmentType];
}

/**
 * Quote totals for summary page
 */
export interface QuoteTotals {
  subtotal: number;
  discount?: number; // Optional discount in euros (absolute value)
  subtotalAfterDiscount: number; // Subtotal after discount applied
  inarcassa?: number; // Optional 4% Inarcassa contribution
  iva: number;
  total: number;
}

/**
 * Creates an empty paragraph with configurable spacing
 * @param spacing - Spacing after the paragraph in DXA units (default: 120)
 * @returns Empty Paragraph instance
 */
export function emptyP(spacing: number = 120): Paragraph {
  return new Paragraph({
    spacing: { after: spacing, before: 0 },
    children: [],
  });
}

/**
 * Creates a text run with font styling
 * @param content - Text content
 * @param options - Text styling options (size, bold, color)
 * @returns TextRun instance with Inter font
 */
export function text(content: string, options: TextOptions = {}): TextRun {
  return new TextRun({
    text: content,
    font: 'Inter',
    size: options.size || 20, // 10pt default (size is in half-points)
    bold: options.bold || false,
    color: options.color || DOCUMENT_COLORS.text,
    ...options,
  });
}

/**
 * Creates a paragraph with text content
 * @param content - Text content (string or array of TextRun)
 * @param options - Paragraph styling options
 * @returns Paragraph instance
 */
export function p(
  content: string | TextRun[],
  options: ParagraphOptions = {}
): Paragraph {
  // Extract text options for the text function
  const textOptions: TextOptions = {
    size: options.size,
    bold: options.bold,
    color: options.color,
  };

  const children =
    typeof content === 'string' ? [text(content, textOptions)] : content;

  return new Paragraph({
    spacing: {
      after: options.after || 120,
      before: options.before || 0,
      line: options.line || 276,
    },
    alignment: options.alignment || AlignmentType.LEFT,
    children,
  });
}

/**
 * Estimated total of a VACATION (T&M) quote: the sum of `rate × estimatedHours`
 * across the junior/mid/senior level rates. Mirrors the `levelRatesEstimatedTotal`
 * computed in `quote-create-form.service.ts`.
 */
export function calculateLevelRatesTotal(
  levelRates: SalesLevelRatesDto
): number {
  const entry = (e: SalesLevelRateEntryDto | undefined): number =>
    (e?.rate ?? 0) * (e?.estimatedHours ?? 0);
  return entry(levelRates.junior) + entry(levelRates.mid) + entry(levelRates.senior);
}

/**
 * Calculates quote totals including subtotal, optional discount, Inarcassa contribution (4%), IVA (22%), and total
 * Formula: subtotal = (baseAmount × complexity) - discount
 *
 * For VACATION (T&M) quotes the base amount is the level-rates estimate
 * (`rate × estimatedHours`), since such quotes carry no `amount`.
 *
 * @param quote - Quote entity
 * @param tenant - Tenant configuration (used to check if Inarcassa contribution is enabled)
 * @returns QuoteTotals object with calculated values
 */
export function calculateQuoteTotals(
  quote: Quote,
  tenant: UsersTenantResponseDto
): QuoteTotals {
  // Base amount: price-list sum for PRICE_LIST quotes, level-rates estimate
  // for VACATION quotes (which have no `amount`).
  const baseAmount =
    quote.compensationMode === 'VACATION' && quote.levelRates
      ? calculateLevelRatesTotal(quote.levelRates)
      : quote.amount || 0;

  // Apply complexity multiplier if present (complexity is a multiplier, e.g., 1.5)
  const complexityMultiplier = quote.complexity || 1;
  const subtotal = baseAmount * complexityMultiplier;

  // Apply discount if present (discount is an absolute value in euros)
  const discount = quote.discount || 0;
  const subtotalAfterDiscount = subtotal - discount;

  // Calculate Inarcassa contribution if enabled (4% of subtotal after discount)
  const inarcassa = tenant.inarcassaContribution
    ? subtotalAfterDiscount * 0.04
    : 0;

  // Calculate IVA at 22% on (subtotal after discount + inarcassa)
  const taxableBase = subtotalAfterDiscount + inarcassa;
  const iva = taxableBase * 0.22;

  // Calculate total
  const total = subtotalAfterDiscount + inarcassa + iva;

  return {
    subtotal,
    ...(discount > 0 ? { discount } : {}), // Only include if present
    subtotalAfterDiscount,
    ...(tenant.inarcassaContribution ? { inarcassa } : {}), // Only include if enabled
    iva,
    total,
  };
}

/**
 * Calculates the export price for an individual price list item.
 * Formula: (base_price + complexities_sum) × complexity_multiplier
 *
 * Note: Discount is NOT applied to individual items, it's only shown in the quote total.
 *
 * @param item - Quote price list item with base price and optional complexities
 * @param complexityMultiplier - Quote complexity multiplier (default: 1)
 * @returns Calculated price with multiplier applied
 */
export function calculateItemExportPrice(
  item: SalesQuotePriceListResponseDto,
  complexityMultiplier: number = 1
): number {
  const basePrice = item.price || 0;
  const complexitiesSum = (item.complexities || []).reduce(
    (sum, complexity) => sum + (complexity.price || 0),
    0
  );
  return (basePrice + complexitiesSum) * complexityMultiplier;
}
